import React, { useState, useEffect, useMemo } from "react";
import { Card, Table, Tooltip, Tag, Space, Typography, Switch, Select, Input, Alert, Button, InputNumber, message } from "antd";
import { ClockCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Sourcing, SourcingStatus, TechnicalEvaluationMode, EvaluationCriteriaType } from "../interface";
import { GET_BIDS_BY_SOURCING_QUERY, UPDATE_BID_EVALUATION_MUTATION, SAVE_ITEM_AWARDS_MUTATION } from "../queries";

const { Text } = Typography;

interface PriceTabulationProps {
  record: Sourcing;
  isFinancialCompleted?: boolean;
}

interface SupplierQuote {
  supplierName: string;
  supplierId: string;
  unitPrice: number;
  deliveryTime: string;
  notes?: string;
  totalPrice: number;
}

interface EvaluationItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  estimatedPrice: number;
  estimatedTotal: number;
  supplierQuotes: { [supplierId: string]: SupplierQuote };
}

interface ItemAwardState {
  decision: string;
  quantity: number;
  remarks: string;
}

export const PriceTabulation: React.FC<PriceTabulationProps> = ({ record, isFinancialCompleted = false }) => {
  const [evaluationData, setEvaluationData] = useState<EvaluationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState<any[]>([]);
  const [savingAwards, setSavingAwards] = useState(false);
  const [supplierEvaluations, setSupplierEvaluations] = useState<Record<string, {compliance: boolean, decision: string, remarks: string}>>({});
  const [supplierToBidMap, setSupplierToBidMap] = useState<Record<string, string>>({});
  const [itemAwards, setItemAwards] = useState<Record<string, Record<string, ItemAwardState>>>({});

  const { mutate: updateBidEvaluation } = useCustomMutation();
  const { mutate: saveItemAwardsMutation } = useCustomMutation();

  // Query for bids - only enable when status is CLOSED
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && record.status === SourcingStatus.CLOSED,
    },
  });

  useEffect(() => {
    if (record?.status === SourcingStatus.CLOSED && record?.id) {
      loadEvaluationData();
    }
  }, [record?.status, record?.id]);

  const loadEvaluationData = async () => {
    if (!record?.id || !refetchBids) return;

    setLoading(true);
    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];
      
      // Store raw bids data in state for technical evaluation section
      setBids(bidsData);

      // Transform data into evaluation format
      const evaluationItems = transformToEvaluationData(record, bidsData);
      setEvaluationData(evaluationItems);

      // Compute auto-filled supplier evaluations
      const supplierIds = new Set<string>();
      evaluationItems.forEach(item => {
        Object.keys(item.supplierQuotes).forEach(supplierId => {
          supplierIds.add(supplierId);
        });
      });
      const suppliers = Array.from(supplierIds);
      const estimatedTotal = evaluationItems.reduce((total, item) => total + item.estimatedTotal, 0);
      const totals = suppliers.map(id => ({ id, total: getTotalForSupplier(id) }));
      const minTotal = Math.min(...totals.map(t => t.total));
      const evaluations: Record<string, { compliance: boolean; decision: string; remarks: string }> = {};
      const supplierToBid: Record<string, string> = {};

      // Load existing values from bids or auto-fill
      bidsData.forEach((bid: any) => {
        const supplierId = bid.supplierId;
        const bidId = bid._id;
        if (suppliers.includes(supplierId)) {
          supplierToBid[supplierId] = bidId;
          const total = getTotalForSupplier(supplierId);
          const compliance = bid.compliance !== null && bid.compliance !== undefined ? Boolean(bid.compliance) : evaluationItems.every(item => {
            const quote = item.supplierQuotes[supplierId];
            return quote && quote.deliveryTime !== 'Not specified';
          });
          const decision = bid.decision || (total === minTotal ? 'Winner' : 'Qualified');
          let remarks = bid.evaluationRemarks || '';
          if (!remarks) {
            if (decision === 'Winner') {
              remarks = 'Lowest bid';
            } else if (total > estimatedTotal * 1.1) {
              remarks = 'High price';
            } else if (total < estimatedTotal * 0.9) {
              remarks = 'Low price';
            } else {
              remarks = 'Competitive price';
            }
          }
          evaluations[bidId] = { compliance, decision, remarks };
        }
      });
      setSupplierEvaluations(evaluations);
      setSupplierToBidMap(supplierToBid);

      const existingItemAwards = (record as any).itemAwards || [];
      const newItemAwards: Record<string, Record<string, ItemAwardState>> = {};
      
      evaluationItems.forEach(item => {
        newItemAwards[item.id] = {};
        suppliers.forEach(supplierId => {
          const existing = existingItemAwards.find(
            (a: any) => a.sourcingItemId === item.id && a.supplierId === supplierId
          );
          newItemAwards[item.id][supplierId] = {
            decision: existing?.decision || '',
            quantity: existing?.quantity || 0,
            remarks: existing?.remarks || '',
          };
        });
      });
      setItemAwards(newItemAwards);
    } catch (error) {
      console.error('Error loading evaluation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const transformToEvaluationData = (sourcing: Sourcing, bids: any[]): EvaluationItem[] => {
    const itemsMap = new Map<string, EvaluationItem>();

    // Initialize items from sourcing
    sourcing.items.forEach(item => {
      itemsMap.set(item.id, {
        id: item.id,
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'Pcs',
        estimatedPrice: item.unitPrice,
        estimatedTotal: item.totalPrice || (item.quantity * item.unitPrice),
        supplierQuotes: {}
      });
    });

    // Add supplier quotes
    bids.forEach(bid => {
      const supplierName = bid.companyName || 'Unknown Supplier';
      const supplierId = bid.supplierId;

      bid.items.forEach((bidItem: any) => {
        const itemId = findMatchingItemId(bidItem, sourcing.items);
        if (itemId && itemsMap.has(itemId)) {
          const evaluationItem = itemsMap.get(itemId)!;
          evaluationItem.supplierQuotes[supplierId] = {
            supplierName,
            supplierId,
            unitPrice: bidItem.unitPrice,
            deliveryTime: bidItem.deliveryTime || 'Not specified',
            notes: bidItem.notes,
            totalPrice: bidItem.total || (bidItem.quantity * bidItem.unitPrice)
          };
        }
      });
    });

    return Array.from(itemsMap.values());
  };

  const findMatchingItemId = (bidItem: any, sourcingItems: any[]): string | null => {
    // Try to match by name first
    const matchingItem = sourcingItems.find(item =>
      item.name.toLowerCase() === bidItem.itemName?.toLowerCase() ||
      item.name.toLowerCase().includes(bidItem.itemName?.toLowerCase()) ||
      bidItem.itemName?.toLowerCase().includes(item.name.toLowerCase())
    );
    return matchingItem?.id || null;
  };

  const getLowestPriceForItem = (evaluationItem: EvaluationItem): number => {
    const prices = Object.values(evaluationItem.supplierQuotes)
      .map(quote => quote.unitPrice)
      .filter(price => price > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

  const getPriceColor = (unitPrice: number, estimatedPrice: number, lowestPrice: number): string => {
    if (unitPrice === 0) return 'default';

    const priceRatio = unitPrice / estimatedPrice;

    if (unitPrice === lowestPrice) return 'success'; // Green for lowest
    if (priceRatio > 1.2) return 'error'; // Red for significantly higher (>20%)
    if (priceRatio < 0.9) return 'warning'; // Orange for lower but not lowest

    return 'default';
  };

  const renderSupplierQuote = (quote: SupplierQuote | undefined, estimatedPrice: number) => {
    if (!quote) {
      return <Text type="secondary">—</Text>;
    }

    const itemWithQuote = evaluationData.find(item =>
      Object.values(item.supplierQuotes).some(q => q.supplierId === quote.supplierId)
    );
    const lowestPrice = itemWithQuote ? getLowestPriceForItem(itemWithQuote) : 0;

    const priceColor = getPriceColor(quote.unitPrice, estimatedPrice, lowestPrice);

    return (
      <Tooltip
        title={
          <div>
            <p><strong>Unit Price:</strong> {record.currency || 'ETB'} {quote.unitPrice.toLocaleString()}</p>
            <p><strong>Delivery Time:</strong> {quote.deliveryTime}</p>
            {quote.notes && <p><strong>Notes:</strong> {quote.notes}</p>}
            <p><strong>Total:</strong> {record.currency || 'ETB'} {quote.totalPrice.toLocaleString()}</p>
          </div>
        }
      >
        <div style={{ textAlign: 'center' }}>
          <Tag color={priceColor} style={{ marginBottom: 4 }}>
            {record.currency || 'ETB'} {quote.unitPrice.toLocaleString()}
          </Tag>
          <div style={{ fontSize: '11px', color: '#666' }}>
            <Space>
              <ClockCircleOutlined />
              {quote.deliveryTime}
              {quote.deliveryTime === 'Not specified' && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
            </Space>
          </div>
        </div>
      </Tooltip>
    );
  };

  const getTotalForSupplier = (supplierId: string): number => {
    return evaluationData.reduce((total, item) => {
      const quote = item.supplierQuotes[supplierId];
      return total + (quote ? quote.totalPrice : 0);
    }, 0);
  };

  const getTotalEstimatedPrice = (): number => {
    return evaluationData.reduce((total, item) => {
      return total + item.estimatedTotal;
    }, 0);
  };

  const getSupplierColumns = () => {
    const ids = new Set<string>();
    evaluationData.forEach(item => {
      Object.keys(item.supplierQuotes).forEach(sid => ids.add(sid));
    });
    const supplierIds = Array.from(ids);
    return supplierIds.flatMap(supplierId => {
      const supplierName = evaluationData
        .flatMap(item => Object.values(item.supplierQuotes))
        .find(quote => quote.supplierId === supplierId)?.supplierName || 'Unknown';

      const priceCol = {
        title: supplierName,
        dataIndex: `supplier_${supplierId}`,
        key: `supplier_${supplierId}`,
        width: 150,
        render: (value: any, row: EvaluationItem) => {
          const quote = row.supplierQuotes[supplierId];
          return renderSupplierQuote(quote, row.estimatedPrice);
        }
      };

      return [priceCol];
    });
  };

  const getAwardColumns = () => {
    const ids = new Set<string>();
    evaluationData.forEach(item => {
      Object.keys(item.supplierQuotes).forEach(sid => ids.add(sid));
    });
    const supplierIds = Array.from(ids);
    
    return supplierIds.flatMap((supplierId, i) => {
      const decisionCol = {
        title: `Decision`,
        dataIndex: `award_decision_${supplierId}`,
        key: `award_decision_${supplierId}`,
        width: 150,
        render: (value: any, row: EvaluationItem) => {
          const award = itemAwards[row.id]?.[supplierId];
          return (
            <Select
              value={award?.decision || ''}
              onChange={(value) => updateItemAwardDecision(row.id, supplierId, value)}
              style={{ width: '100%' }}
              size="small"
              disabled={isFinancialCompleted}
              placeholder="Select"
              options={[
                { value: 'Winner', label: 'Winner' },
                { value: 'Qualified', label: 'Qualified' },
                { value: 'Disqualified', label: 'Disqualified' }
              ]}
            />
          );
        }
      };

      const qtyCol = {
        title: `Qty`,
        dataIndex: `award_qty_${supplierId}`,
        key: `award_qty_${supplierId}`,
        width: 100,
        render: (value: any, row: EvaluationItem) => {
          const award = itemAwards[row.id]?.[supplierId];
          const otherAwarded = Object.entries(itemAwards[row.id] || {})
            .filter(([sid]) => sid !== supplierId)
            .reduce((sum, [, v]) => sum + v.quantity, 0);
          const maxQty = Math.max(0, row.quantity - otherAwarded);
          
          return award?.decision === 'Winner' ? (
            <InputNumber
              value={award?.quantity || 0}
              onChange={(val) => updateItemAwardQuantity(row.id, supplierId, val || 0)}
              min={0}
              max={maxQty}
              size="small"
              style={{ width: '100%' }}
              disabled={isFinancialCompleted}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
          );
        }
      };

      return [decisionCol, qtyCol];
    });
  };

  const getSupplierIds = () => {
    const ids = new Set<string>();
    evaluationData.forEach(item => {
      Object.keys(item.supplierQuotes).forEach(sid => ids.add(sid));
    });
    return Array.from(ids);
  };

  const updateCompliance = (supplierId: string, compliance: boolean) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], compliance }
    }));
    const currentEvaluation = supplierEvaluations[bidId];
    handleSaveEvaluationWithValue(bidId, { ...currentEvaluation, compliance });
  };

  const updateDecision = (supplierId: string, decision: string) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], decision }
    }));
  };

  const updateRemarks = (supplierId: string, remarks: string) => {
    const bidId = supplierToBidMap[supplierId];
    if (!bidId) return;
    setSupplierEvaluations(prev => ({
      ...prev,
      [bidId]: { ...prev[bidId], remarks }
    }));
  };

  const handleSaveEvaluation = (bidId: string) => {
    const evaluation = supplierEvaluations[bidId];
    if (!evaluation) return;
    handleSaveEvaluationWithValue(bidId, evaluation);
  };

  const handleSaveEvaluationWithValue = (bidId: string, evaluation: { compliance: boolean; decision: string; remarks: string }) => {
    updateBidEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            bidId,
            compliance: evaluation.compliance,
            decision: evaluation.decision,
            evaluationRemarks: evaluation.remarks,
          },
        },
        meta: { gqlMutation: UPDATE_BID_EVALUATION_MUTATION },
      },
      {
        onError: (error: any) => {
          console.error('Save evaluation error:', error);
        },
      },
    );
  };

  const updateItemAwardDecision = (itemId: string, supplierId: string, decision: string) => {
    setItemAwards(prev => {
      const newState = { ...prev };
      if (!newState[itemId]) newState[itemId] = {};
      if (!newState[itemId][supplierId]) newState[itemId][supplierId] = { decision: '', quantity: 0, remarks: '' };
      newState[itemId] = { ...newState[itemId] };
      newState[itemId][supplierId] = { ...newState[itemId][supplierId], decision };
      
      if (decision === 'Winner' && newState[itemId][supplierId].quantity === 0) {
        const item = evaluationData.find(i => i.id === itemId);
        if (item) {
          const otherAwarded = Object.entries(newState[itemId] || {})
            .filter(([sid]) => sid !== supplierId)
            .reduce((sum, [, v]) => sum + v.quantity, 0);
          const remaining = item.quantity - otherAwarded;
          newState[itemId][supplierId] = { ...newState[itemId][supplierId], quantity: Math.max(0, remaining) };
        }
      }
      if (decision !== 'Winner') {
        newState[itemId][supplierId] = { ...newState[itemId][supplierId], quantity: 0 };
      }
      
      return newState;
    });
  };

  const updateItemAwardQuantity = (itemId: string, supplierId: string, quantity: number) => {
    setItemAwards(prev => {
      const newState = { ...prev };
      if (!newState[itemId]) newState[itemId] = {};
      if (!newState[itemId][supplierId]) newState[itemId][supplierId] = { decision: '', quantity: 0, remarks: '' };
      newState[itemId] = { ...newState[itemId] };
      newState[itemId][supplierId] = { ...newState[itemId][supplierId], quantity };
      return newState;
    });
  };

  const getTotalAwardedQty = (itemId: string): number => {
    const supplierMap = itemAwards[itemId] || {};
    return Object.values(supplierMap).reduce((sum, a) => sum + a.quantity, 0);
  };

  const handleSaveItemAwards = () => {
    const awards: any[] = [];
    evaluationData.forEach(item => {
      const supplierMap = itemAwards[item.id] || {};
      Object.entries(supplierMap).forEach(([supplierId, award]) => {
        if (award.decision) {
          const bid = bids.find(b => b.supplierId === supplierId);
          const quote = item.supplierQuotes[supplierId];
          awards.push({
            sourcingItemId: item.id,
            bidId: bid ? bid._id : '',
            supplierId: supplierId,
            supplierName: quote?.supplierName || supplierId,
            quantity: award.quantity || 0,
            unitPrice: quote?.unitPrice || 0,
            totalPrice: award.quantity * (quote?.unitPrice || 0),
            decision: award.decision,
            remarks: award.remarks || '',
          });
        }
      });
    });

    setSavingAwards(true);
    saveItemAwardsMutation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
            itemAwards: awards,
          },
        },
        meta: { gqlMutation: SAVE_ITEM_AWARDS_MUTATION },
      },
      {
        onSuccess: () => {
          message.success('Item awards saved successfully');
          setSavingAwards(false);
        },
        onError: (error: any) => {
          message.error('Failed to save item awards');
          console.error('Save item awards error:', error);
          setSavingAwards(false);
        },
      },
    );
  };

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      width: 200,
      render: (name: string, record: EvaluationItem) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.description}</div>
        </div>
      )
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (quantity: number, row: EvaluationItem) => {
        const awarded = getTotalAwardedQty(row.id);
        return (
          <div>
            <div>{quantity.toLocaleString()}</div>
            {awarded > 0 && (
              <Tag color={awarded >= quantity ? 'green' : 'blue'} style={{ fontSize: 10 }}>
                {awarded}/{quantity}
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Est. Price',
      dataIndex: 'estimatedPrice',
      key: 'estimatedPrice',
      width: 120,
      render: (price: number) => (
        <Text type="secondary">
          {record.currency || 'ETB'} {price.toLocaleString()}
        </Text>
      )
    },
    ...getSupplierColumns(),
    ...getAwardColumns()
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading evaluation data...
        </div>
      </Card>
    );
  }

  if (evaluationData.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          No bids have been submitted for this sourcing event yet.
        </div>
      </Card>
    );
  }

  // Check if technical evaluation is enabled
  const hasTechnicalEvaluation = record.technicalEvaluationMode !== TechnicalEvaluationMode.NONE;
  const technicalCriteria = record.evaluationCriterias?.filter(c => c.type === EvaluationCriteriaType.TECHNICAL) || [];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Financial Evaluation Completed Notice */}
      {isFinancialCompleted && (
        <Alert
          message="Financial Evaluation Completed"
          description="All inputs are disabled as financial evaluation has been completed."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      
      {/* Technical Evaluation Section for RFQ */}
      {hasTechnicalEvaluation && technicalCriteria.length > 0 && (
        <Card title={
          <Space>
            <Text>Technical Evaluation</Text>
            {record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL && (
              <Tag color="blue">PASS/FAIL Mode</Tag>
            )}
            {record.technicalEvaluationMode === TechnicalEvaluationMode.MATRIX && (
              <Tag color="purple">Lightweight MATRIX Mode</Tag>
            )}
          </Space>
        } type="inner">
          <Space direction="vertical" style={{ width: '100%' }}>
            {bids.map((bid: any) => {
              const technicalScore = bid?.technicalScore || 0;
              const evaluationStatus = bid?.evaluationStatus;

              return (
                <Card key={bid._id} size="small" style={{ marginBottom: '8px' }}>
                  <Space align="center">
                    <Text strong>{bid.companyName}</Text>
                    <Tag color={technicalScore >= (record.technicalEvaluationThreshold || 0) ? 'success' : 'error'}>
                      Technical Score: {technicalScore}/100
                    </Tag>
                    <Tag color={evaluationStatus === 'QUALIFIED' ? 'success' : evaluationStatus === 'DISQUALIFIED' ? 'error' : 'default'}>
                      {evaluationStatus || 'Not Evaluated'}
                    </Tag>
                    {record.technicalEvaluationMode === TechnicalEvaluationMode.PASS_FAIL && (
                      <Space>
                        {technicalScore >= (record.technicalEvaluationThreshold || 0) ?
                          <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        }
                        <Text>{technicalScore >= (record.technicalEvaluationThreshold || 0) ? 'Pass' : 'Fail'}</Text>
                      </Space>
                    )}
                  </Space>
                </Card>
              );
            })}
          </Space>
        </Card>
      )}

      <Card title="Price Tabulation" type="inner">
        <Table
          dataSource={evaluationData}
          columns={columns}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={false}
          size="small"
          summary={() => {
            const supplierIds = getSupplierIds();
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <strong>
                      {record.currency || 'ETB'} {getTotalEstimatedPrice().toLocaleString()}
                    </strong>
                  </Table.Summary.Cell>
                  {supplierIds.map((supplierId, i) => {
                    const total = getTotalForSupplier(supplierId);
                    return (
                      <Table.Summary.Cell index={3 + i} key={supplierId}>
                        <strong>
                          {record.currency || 'ETB'} {total.toLocaleString()}
                        </strong>
                      </Table.Summary.Cell>
                    );
                  })}
                  {(() => {
                    const cells: React.ReactNode[] = [];
                    const baseCols = 3 + supplierIds.length;
                    supplierIds.forEach((supplierId, idx) => {
                      const bidId = supplierToBidMap[supplierId];
                      cells.push(
                        <Table.Summary.Cell key={`dec-${supplierId}`} index={baseCols + idx * 2}>
                          <strong>Compliance</strong>
                        </Table.Summary.Cell>
                      );
                      cells.push(
                        <Table.Summary.Cell key={`com-${supplierId}`} index={baseCols + idx * 2 + 1}>
                          <Switch
                            checked={bidId ? (supplierEvaluations[bidId]?.compliance ?? false) : false}
                            onChange={(checked) => updateCompliance(supplierId, checked)}
                            disabled={isFinancialCompleted}
                            size="small"
                          />
                        </Table.Summary.Cell>
                      );
                    });
                    return cells;
                  })()}
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>Remarks</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Text type="secondary">Evaluation Remarks</Text>
                  </Table.Summary.Cell>
                  {supplierIds.map((supplierId, i) => {
                    const total = getTotalForSupplier(supplierId);
                    return (
                      <Table.Summary.Cell index={3 + i} key={`total-${supplierId}`}>
                        <Text type="secondary">
                          {total.toLocaleString()}
                        </Text>
                      </Table.Summary.Cell>
                    );
                  })}
                  {(() => {
                    const cells: React.ReactNode[] = [];
                    const baseCols = 3 + supplierIds.length;
                    supplierIds.forEach((supplierId, idx) => {
                      const bidId = supplierToBidMap[supplierId];
                      cells.push(
                        <Table.Summary.Cell key={`rmk1-${supplierId}`} index={baseCols + idx * 2}>
                          <strong>Remarks</strong>
                        </Table.Summary.Cell>
                      );
                      cells.push(
                        <Table.Summary.Cell key={`rmk2-${supplierId}`} index={baseCols + idx * 2 + 1}>
                          <Input.TextArea
                            value={bidId ? (supplierEvaluations[bidId]?.remarks || '') : ''}
                            onChange={(e) => updateRemarks(supplierId, e.target.value)}
                            onBlur={() => bidId && handleSaveEvaluation(bidId)}
                            autoSize={{ minRows: 1, maxRows: 2 }}
                            size="small"
                            disabled={isFinancialCompleted}
                          />
                        </Table.Summary.Cell>
                      );
                    });
                    return cells;
                  })()}
                </Table.Summary.Row>
              </>
            );
          }}
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveItemAwards}
            loading={savingAwards}
            disabled={isFinancialCompleted}
          >
            Save Item Awards
          </Button>
        </div>
      </Card>

      <Card title="Evaluation Legend" size="small">
        <Space direction="vertical">
          <div><Tag color="success">Green</Tag> - Lowest price for item</div>
          <div><Tag color="error">Red</Tag> - Significantly higher than estimate {'>'}20%</div>
          <div><Tag color="warning">Orange</Tag> - Lower than estimate but not lowest</div>
          <div><ClockCircleOutlined style={{ color: '#faad14' }} /> - Delivery time not specified</div>
          {hasTechnicalEvaluation && (
            <>
              <div><CheckCircleOutlined style={{ color: '#52c41a' }} /> Pass / <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Fail - Technical evaluation result</div>
              <div>RFQ maintains price dominance with optional lightweight technical evaluation</div>
            </>
          )}
        </Space>
      </Card>
    </Space>
  );
};